import { Link } from '@tanstack/react-router';

import { Button } from '@/components/ui/button';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { usePermissions } from '@/lib/hooks/usePermissions';
import { cn } from '@/lib/utils';

export function UsagePage() {
  const { data: perms, isLoading } = usePermissions();

  return (
    <div className="mx-auto max-w-6xl px-8 py-10">
      <header className="flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Usage</h1>
          <p className="mt-1 text-sm text-slate-500">
            Resource consumption against your{' '}
            <strong className="uppercase tracking-wide">{perms?.tier ?? '—'}</strong> plan caps.
          </p>
        </div>
        <Button asChild>
          <Link to="/app/billing">Upgrade plan</Link>
        </Button>
      </header>

      {isLoading && <p className="mt-6 text-sm text-slate-500">Loading…</p>}

      {perms && (
        <>
          <h2 className="mt-8 mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
            This billing period
          </h2>
          <div className="grid gap-4 md:grid-cols-1">
            <UsageMeter
              label="Monitored PRs"
              used={perms.usage.monitored_prs_this_period}
              limit={perms.caps.monitored_prs_per_month}
              hint="Resets monthly. Past 80% triggers an alert email; past 100% Athena reviews PRs but stops posting comments until next period."
            />
          </div>

          <h2 className="mt-8 mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
            Resources
          </h2>
          <div className="grid gap-4 md:grid-cols-3">
            <UsageMeter
              label="OpenAPI specs"
              used={perms.usage.specs}
              limit={perms.caps.specs}
            />
            <UsageMeter
              label="SDK repos"
              used={perms.usage.sdk_repos}
              limit={perms.caps.sdk_repos}
            />
            <UsageMeter
              label="Docs domains"
              used={perms.usage.docs_domains}
              limit={perms.caps.docs_domains}
            />
          </div>

          <Card className="mt-8">
            <CardHeader>
              <CardTitle>Where to look next</CardTitle>
              <CardDescription>
                <Link to="/app/specs" className="underline">Specs</Link>
                {' · '}
                <Link to="/app/pr-checks" className="underline">PR checks</Link>
                {' · '}
                <Link to="/app/billing" className="underline">Billing & invoices</Link>
              </CardDescription>
            </CardHeader>
          </Card>
        </>
      )}
    </div>
  );
}

function UsageMeter({
  label,
  used,
  limit,
  hint,
}: {
  label: string;
  used: number;
  limit: number;
  hint?: string;
}) {
  const unlimited = limit === -1;
  const pct = unlimited || limit === 0 ? 0 : Math.min(100, Math.round((used / limit) * 100));
  const tone =
    unlimited ? 'safe'
    : pct >= 100 ? 'critical'
    : pct >= 80 ? 'warn'
    : 'safe';
  const barColor = {
    safe: 'bg-[var(--color-accent)]',
    warn: 'bg-[var(--color-warning)]',
    critical: 'bg-[var(--color-critical)]',
  }[tone];
  const numColor = {
    safe: 'text-slate-900 dark:text-slate-100',
    warn: 'text-[var(--color-warning)]',
    critical: 'text-[var(--color-critical)]',
  }[tone];

  return (
    <Card>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className={cn('mt-2 text-3xl font-semibold', numColor)}>
        {used.toLocaleString()}
        <span className="ml-1 text-base font-normal text-slate-400">
          / {unlimited ? '∞' : limit.toLocaleString()}
        </span>
      </div>
      {!unlimited && limit > 0 && (
        <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
          <div
            className={cn('h-full rounded-full transition-all', barColor)}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
      {hint && <p className="mt-3 text-xs text-slate-500">{hint}</p>}
    </Card>
  );
}
